import { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Spinner,
  Switch,
  toast,
} from "@heroui/react";
import { useMyBlacklistSettings, useUpdateBlacklistSettings } from "./use-blacklist";

export type ShareBlacklistSettingsFormProps = {
  onSuccess: () => void;
  onCancel?: () => void;
  initialTitle?: string;
  initialIsPublic?: boolean;
};

export function ShareBlacklistSettingsForm({
  onSuccess,
  onCancel,
  initialTitle = "",
  initialIsPublic = false,
}: ShareBlacklistSettingsFormProps) {
  const [title, setTitle] = useState(initialTitle);
  const [isPublic, setIsPublic] = useState(initialIsPublic);
  const [agreed, setAgreed] = useState(false);

  // 当前公开设置走缓存；首次拉到后只回填一次表单（之后后台 refetch 不能覆盖用户正在编辑的内容），失败则沿用初始值
  const settingsQuery = useMyBlacklistSettings();
  const fetchLoading = settingsQuery.isPending;
  const syncedRef = useRef(false);
  useEffect(() => {
    if (settingsQuery.isPending || syncedRef.current) return;
    syncedRef.current = true;
    if (settingsQuery.data) {
      setTitle(settingsQuery.data.title ?? initialTitle);
      setIsPublic(settingsQuery.data.is_public ?? initialIsPublic);
    } else {
      setTitle(initialTitle);
      setIsPublic(initialIsPublic);
    }
  }, [settingsQuery.isPending, settingsQuery.data, initialTitle, initialIsPublic]);

  const updateSettings = useUpdateBlacklistSettings();
  const saveLoading = updateSettings.isPending;

  const handleSave = () => {
    if (!agreed) {
      toast.danger("请先同意共享条款");
      return;
    }
    updateSettings.mutate({ title, is_public: isPublic }, { onSuccess });
  };

  if (fetchLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-gray-600 text-sm">
        您可以使用共享功能，将您的黑名单共享到「公共黑名单」中，供其他人订阅使用。
      </p>
      <div className="flex flex-col gap-1 mx-1">
        <Label>对外展示名称</Label>
        <Input
          value={title}
          placeholder="建议统一名称：XXXXXX的黑名单"
          onChange={(e) => setTitle(e.target.value.slice(0, 16))}
          maxLength={16}
          disabled={saveLoading}
        />
        <span className="text-xs text-gray-500">{title.length}/16</span>
      </div>
      <div className="flex items-center justify-between">
        <Switch isSelected={isPublic} onChange={setIsPublic} isDisabled={saveLoading}>
          <Switch.Control>
            <Switch.Thumb />
          </Switch.Control>
          <Switch.Content>
            <Label className="text-sm">共享至公共黑名单</Label>
          </Switch.Content>
        </Switch>
      </div>
      <div className="p-4 bg-gray-200">
        <div className="mb-4">
          <h6 className="mb-2 text-sm font-bold">共享黑名单使用准则</h6>
          <ol className="text-sm text-gray-500 flex flex-col gap-2">
            <li>
              1. 不得用于任何形式的商业、贿赂行为。被他人举报核实的，永久不得使用共享系统。
            </li>
            <li>
              2. 始终保持公平、公正，不得用于单方面制裁、报复行为。被他人举报核实的，永久不得使用共享系统。
            </li>
            <li>
              3.
              始终为自己的数据负责，保证证据链条完整。在他人申诉时，随时应官方要求提供有效证据链。不能提供有效证据的，一年内超过3次将冻结使用权限。
            </li>
            <li>4. 除上述情节外的其他情节严重的，将冻结模板服务使用权限。</li>
          </ol>
        </div>
        <Checkbox
          isSelected={agreed}
          onChange={(checked) => setAgreed(checked)}
          isDisabled={saveLoading}
        >
          <Checkbox.Content>
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <span className="text-sm text-gray-600">我同意并遵循上述共享准则。</span>
          </Checkbox.Content>
        </Checkbox>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel ? (
          <Button variant="tertiary" onPress={onCancel} isDisabled={saveLoading}>
            关闭
          </Button>
        ) : null}
        <Button
          variant="primary"
          onPress={handleSave}
          isPending={saveLoading}
          isDisabled={!agreed}
        >
          {({ isPending }) => (
            <>
              {isPending && <Spinner size="sm" color="current" />}
              保存
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
